// Extend Window interface to include PostHog
declare global {
  interface Window {
    posthog?: {
      capture: (eventName: string, properties?: Record<string, any>) => void;
      identify: (distinctId: string, properties?: Record<string, any>) => void;
    };
  }
}

// Generic function to capture input events
const captureInputEvent = (eventName: string, fieldName: string, fieldType: string, fieldValue: string) => {
  // Use global PostHog instance if available
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(eventName, {
      'data-mg-field-name': fieldName,
      'data-mg-field-type': fieldType,
      'data-mg-field-value': fieldValue,
    });
  }
};



// Simple function to capture text content from elements with testID
const captureTextContent = (element: HTMLElement) => {
  const testId = element.getAttribute('data-testid');
  if (!testId) return;

  const textContent = element.textContent?.trim();
  if (!textContent || textContent.length === 0) return;

  // Use global PostHog instance if available
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture('text_'+testId, {
      'data-mg-field-name': testId,
      'data-mg-field-value': textContent
    });
  }
};

// Function to set up input value capture for web environment
export const setupInputValueCapture = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  // Capture existing text elements with testID on page load
  // Use longer delay to allow for data loading
  setTimeout(() => {
    const elements = document.querySelectorAll('[data-testid]');
    elements.forEach((element) => {
      const htmlElement = element as HTMLElement;
      // Only capture if it has text content and is not an input/button
      if (htmlElement.textContent && 
          htmlElement.tagName !== 'INPUT' && 
          htmlElement.tagName !== 'BUTTON' &&
          !htmlElement.querySelector('input') &&
          !htmlElement.querySelector('button')) {
        // Skip capturing if text is just placeholder values
        const textContent = htmlElement.textContent.trim();
        if (textContent !== 'N/A' && textContent !== '0.00' && textContent !== '') {
          captureTextContent(htmlElement);
        }
      }
    });
  }, 3000); // Increased delay to 3 seconds

  // Also use MutationObserver to capture text when it changes from placeholder to real data
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'childList' || mutation.type === 'characterData') {
        const target = mutation.target as HTMLElement;
        const element = target.nodeType === Node.TEXT_NODE ? target.parentElement : target;
        
        if (element && element.getAttribute('data-testid')) {
          const textContent = element.textContent?.trim();
          // Only capture if text has meaningful content (not placeholder values)
          if (textContent && 
              textContent !== 'N/A' && 
              textContent !== '0.00' && 
              textContent !== '' &&
              element.tagName !== 'INPUT' && 
              element.tagName !== 'BUTTON' &&
              !element.querySelector('input') &&
              !element.querySelector('button')) {
            // Small delay to ensure the change is complete
            setTimeout(() => captureTextContent(element), 100);
          }
        }
      }
    });
  });

  // Start observing text changes
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true
  });

  // Handle React Native TextInput components (blur events)
  document.addEventListener('blur', (event) => {
    const target = event.target as HTMLInputElement;
    if (target && target.tagName === 'INPUT') {
      // Check if this is a React Native TextInput (has data-testid but no name/id)
      const hasTestId = target.getAttribute('data-testid');
      const hasNameOrId = target.name || target.id;
      const isReactNativeInput = hasTestId && !hasNameOrId;
      
      if (isReactNativeInput) {
        const fieldName = hasTestId;
        const fieldType = target.type || 'text';
        const fieldValue = target.value;

        // Only capture non-sensitive input types
        if (!['password', 'hidden'].includes(fieldType)) {
          captureInputEvent('input_' + fieldName, fieldName, fieldType, fieldValue);
        }
      }
    }
  }, true);

  // Handle React Native TouchableOpacity dropdown/option selections
  document.addEventListener('click', (event) => {
    const target = event.target as HTMLElement;
    
    // Simple check: if element has testID, capture it
    const testId = target.getAttribute('data-testid');
    if (testId && testId.trim()) {
      const fieldName = testId;
      const fieldValue = target.textContent || target.innerText || '';
      
      if (fieldValue.trim()) {
        captureInputEvent('input_' + fieldName, fieldName, 'dropdown', fieldValue.trim());
      }
    } else {
      // Check if parent has testID
      const parentTestId = target.parentElement?.getAttribute('data-testid');
      if (parentTestId && parentTestId.trim()) {
        const fieldName = parentTestId;
        const fieldValue = target.textContent || target.innerText || '';
        
        if (fieldValue.trim()) {
          captureInputEvent('input_' + fieldName, fieldName, 'dropdown', fieldValue.trim());
        }
      }
    }
  }, true);
};

// Simple manual function to capture text content from specific testID
export const captureTextByTestId = (testId: string) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return;
  }

  const element = document.querySelector(`[data-testid="${testId}"]`) as HTMLElement;
  if (element) {
    captureTextContent(element);
  }
};


